import { CARRIERS } from '../ShipmentForm/constants';
import {
  AdditionalPartField, AdditionalPartValues, Part, PartUsage, PartEntry, PartV3,
} from './types.d';
import { NEW_PART_EXCLUDED_FIELDS } from './constants';

export { getWorkorderStatusId } from '../utils/partsUtils';

export const RETURN_INSTRUCTION_OPTIONS_RETURNABLE = [
  { label: 'Return the part within 24 hours', value: 'Return within 24 hours' },
  { label: 'Return the part within 48 hours', value: 'Return within 48 hours' },
  { label: 'Return the part within 3 days', value: 'Return within 3 days' },
  { label: 'Return the part within 5 days', value: 'Return within 5 days' },
];

export const RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE = [
  { label: 'Leave the part on site', value: 'Leave part on site' },
  { label: 'Dispose of the part (reuse, recycle, or discard)', value: 'Dispose part' },
];

export const SELECT_OPTIONS = [{
  label: 'Yes',
  value: 'Yes',
}, {
  label: 'No',
  value: 'No',
}, {
  label: '',
  value: '',
}];

export const RETAINED_BY_CUSTOMER_OPTIONS = [
  {
    label: 'Yes',
    value: '1',
  },
  {
    label: 'No',
    value: '0',
  },
];

export const USAGE_CODES = [
  'Used',
  'Not used',
  'Not received',
  'Does not work',
  'Damaged in transit',
  'Wrong part',
];

export const getInitialRetainedByCustomer = (
  customerRetained: string,
): string => {
  if (!customerRetained) {
    return '';
  }
  if (customerRetained === 'Yes') {
    return '1';
  }
  return '0';
};

// Returns true when all required additional fields are filled.
// rma_number is excluded from the new part section — it only applies to defective parts.
export const validateAdditionalFields = (
  fields: AdditionalPartField[] | undefined,
  values: AdditionalPartValues | undefined,
  usageCode: string | undefined,
): boolean => {
  const fieldList = fields || [];

  const newPartBlocking = fieldList
    .filter((f) => f.required && !NEW_PART_EXCLUDED_FIELDS.includes(f.field_name))
    .some((f) => !String(values?.new?.[f.field_name] ?? '').trim());
  if (newPartBlocking) return false;

  if (usageCode) {
    const defectiveBlocking = fieldList
      .filter((f) => f.required)
      .some((f) => !String(values?.defective?.[f.field_name] ?? '').trim());
    if (defectiveBlocking) return false;
  }

  return true;
};

export const getUsageHeader = (
  externalId = '',
  externalNumber = '',
  description = '',
): string => {
  if (!externalId && !externalNumber && !description) {
    return '-';
  }
  return `${externalId} - ${externalNumber} - ${description}`;
};

export const isPartDraftValid = (part: Part): boolean => {
  if (
    !part?.externalId?.trim() ||
    !part?.externalNumber?.trim() ||
    !part?.description?.trim() ||
    !part?.returnable?.trim() ||
    !part?.trackingNumber?.trim() ||
    !part?.returnInstruction
  ) {
    return false;
  }

  if (!part?.carrier || (part.carrier === 'other' && !part?.carrierOther?.trim())) {
    return false;
  }

  return true;
};

export const formatReturnCell = (part: Part): string => {
  if (!part?.returnable) {
    return '-';
  }

  const instruction = part?.returnInstruction || '';
  const shortInstruction = instruction.replace(/^Return\s+/i, '').toLowerCase();

  return shortInstruction ? `${part.returnable} - ${shortInstruction}` : part.returnable;
};

export const nextPartId = (parts: PartEntry[]): number => Math.max(0, ...parts.map((entry) => entry.id)) + 1;

export const convertPartDataToV1Payload = (
  newPart: Part,
  partUsageData: PartUsage,
): PartV3 => {
  const isReturnable = newPart?.returnable === 'Yes';
  const partData = {
    external_id: newPart.externalId,
    description: newPart.description,
    external_number: newPart.externalNumber,
    return_policy: {
      is_returnable: isReturnable,
      ...(newPart?.returnInstruction && {
        return_instruction: newPart.returnInstruction,
      }),
    },
    note: partUsageData?.note || '',
    incoming_tracking: {},
    return_tracking: {},
  } as PartV3;

  if (partUsageData?.usageCode) {
    partData.usage_code = {
      name: partUsageData.usageCode,
    };
  }

  if (partUsageData?.additionalPartValues) {
    partData.additional_part_values = partUsageData.additionalPartValues;
  }

  if (newPart?.trackingNumber) {
    partData.incoming_tracking = {
      tracking_id: newPart.trackingNumber,
      carrier_name: newPart?.carrier === 'other' ?
        newPart?.carrierOther : CARRIERS.find((carrier) => carrier.value === newPart?.carrier)?.label || '',
    };
  }

  const customerRetainedInt = parseInt(partUsageData?.customerRetained, 10) || 0;
  if (!customerRetainedInt && partUsageData?.returnTracking) {
    partData.return_tracking = {
      tracking_id: partUsageData.returnTracking || '',
      carrier_name:
        partUsageData.returnCarrier === 'other' ?
          partUsageData.returnCarrierOtherName : CARRIERS.find((carrier) => carrier.value === partUsageData?.returnCarrier)?.label || '',
      not_returned_reason: null,
    };
  } else {
    partData.return_tracking = {
      tracking_id: '',
      carrier_name: '',
      not_returned_reason: customerRetainedInt === 1 ? 'Customer retained' : null,
    };
  }
  return partData;
};
